// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/components/TopBar.tsx
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { Building2, Check, ChevronsUpDown, CreditCard, LogOut, User } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useAuth, type EmpresaInfo } from '@/hooks/useAuth';

interface AssinaturaStatus {
  lifecycle: string;
  trial: boolean;
  trialExpiraEm?: string | null;
  trialDiasRestantes?: number | null;
}

export function TopBar() {
  const { user, signOut, minhasEmpresas, trocarEmpresa } = useAuth();
  const { toast } = useToast();

  const [empresas, setEmpresas] = useState<EmpresaInfo[]>([]);
  const [trocando, setTrocando] = useState(false);
  const [assinatura, setAssinatura] = useState<AssinaturaStatus | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Carrega as empresas do usuário logado (para o seletor de troca).
  useEffect(() => {
    minhasEmpresas().then(setEmpresas).catch(() => setEmpresas([]));
    // Situação da assinatura (banner de trial).
    apiClient.get('/api/assinatura/status')
      .then(({ data }) => setAssinatura(data))
      .catch(() => setAssinatura(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Conversão trial→pago: pede o link ao Hub e abre o checkout do Mercado Pago.
  const handleAssinar = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await apiClient.post('/api/assinatura/checkout');
      if (data?.initPoint) window.open(data.initPoint, '_blank', 'noopener');
    } catch (err) {
      toast({
        title: 'Link de pagamento indisponível',
        description: err instanceof Error ? err.message : 'Tente novamente ou fale com o suporte.',
        variant: 'destructive',
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const initials = user?.nome
    ? user.nome.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
    : 'U';

  const nomeEmpresa = (e: { nomeFantasia?: string; razaoSocial: string }) =>
    e.nomeFantasia ?? e.razaoSocial;

  const handleTrocar = async (id: number) => {
    if (id === user?.empresa.id || trocando) return;
    setTrocando(true);
    const { error } = await trocarEmpresa(id);   // recarrega a página em caso de sucesso
    if (error) setTrocando(false);
  };

  const temVarias = empresas.length > 1;

  return (
    <header className="h-14 border-b bg-background flex items-center justify-between px-4 shrink-0">
      <div className="flex items-center gap-2">
        <SidebarTrigger />
      </div>

      {/* Empresa ativa — seletor de troca quando há mais de uma */}
      {user?.empresa && (
        temVarias ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="hidden sm:flex h-10 items-center gap-2 px-3" disabled={trocando}>
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-sm font-medium leading-none max-w-[220px] truncate">
                    {nomeEmpresa(user.empresa)}
                  </span>
                  {user.empresa.cnpjCpf && (
                    <span className="text-xs text-muted-foreground">{user.empresa.cnpjCpf}</span>
                  )}
                </div>
                <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72" align="center">
              <DropdownMenuLabel>Trocar de empresa</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {empresas.map((emp) => (
                <DropdownMenuItem
                  key={emp.id}
                  className="cursor-pointer"
                  onClick={() => handleTrocar(emp.id)}
                  disabled={trocando}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${emp.id === user.empresa.id ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm">{nomeEmpresa(emp)}</span>
                    {emp.cnpjCpf && (
                      <span className="text-xs text-muted-foreground">{emp.cnpjCpf}</span>
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="hidden sm:flex flex-col items-center">
            <span className="text-sm font-medium leading-none">{nomeEmpresa(user.empresa)}</span>
            {user.empresa.cnpjCpf && (
              <span className="text-xs text-muted-foreground">{user.empresa.cnpjCpf}</span>
            )}
          </div>
        )
      )}

      <div className="flex items-center gap-3">
        {/* Banner de trial: dias restantes + conversão em assinatura paga */}
        {assinatura?.trial && (
          <div className="hidden md:flex items-center gap-2">
            <span
              className={`text-xs font-medium rounded-full px-2.5 py-1 ${
                (assinatura.trialDiasRestantes ?? 0) <= 3
                  ? 'bg-destructive/10 text-destructive'
                  : 'bg-primary/10 text-primary'
              }`}
            >
              Teste: {assinatura.trialDiasRestantes ?? 0} dia{(assinatura.trialDiasRestantes ?? 0) === 1 ? '' : 's'} restante{(assinatura.trialDiasRestantes ?? 0) === 1 ? '' : 's'}
            </span>
            <Button size="sm" onClick={handleAssinar} disabled={checkoutLoading}>
              <CreditCard className="mr-1.5 h-3.5 w-3.5" />
              {checkoutLoading ? 'Gerando...' : 'Assinar'}
            </Button>
          </div>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.nome ?? 'Usuário'}</p>
                <p className="text-xs leading-none text-muted-foreground">{user?.login}</p>
                {user?.empresa && (
                  <p className="text-xs leading-none text-muted-foreground pt-1">
                    {nomeEmpresa(user.empresa)}
                  </p>
                )}
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem className="cursor-pointer">
              <User className="mr-2 h-4 w-4" />
              Meu perfil
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="cursor-pointer text-destructive focus:text-destructive"
              onClick={signOut}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
